import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { Icon, type IconName } from '@/components/Icon'
import { Tooltip } from '@/components/Tooltip'
import { match } from '@/utils/pattern-match-util'
import styles from './Segmented.module.css'

export interface SegmentedOption<T> {
  value: T
  label: string
  icon?: IconName
  /**
   * Marks the option as having a problem, explained by its tooltip on hover
   * and keyboard focus.
   */
  error?: string
  testId?: string
}

export interface SegmentedProps<T> {
  options: SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  /**
   * `large` switches between the views of a page, like an editor's tabs.
   */
  size?: 'base' | 'large'
  'aria-label'?: string
}

/**
 * The segmented control used for sub-views inside a section.
 */
export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  size = 'base',
  'aria-label': ariaLabel,
}: SegmentedProps<T>) {
  return (
    <span
      className={cx(
        styles.segmented,
        match(size, {
          base: undefined,
          large: styles.large,
        }),
      )}
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const tab = (
          <RawButton
            key={option.value}
            role="tab"
            aria-selected={option.value === value}
            className={cx(
              styles.option,
              match(size, {
                base: styles.baseOption,
                large: styles.largeOption,
              }),
              option.error && styles.invalid,
            )}
            onPress={() => onChange(option.value)}
            testId={option.testId}
          >
            {option.icon && (
              <Icon
                name={option.icon}
                size={14}
                className={styles.icon}
                aria-hidden="true"
              />
            )}
            {option.label}
            {option.error && (
              <span className={styles.errorMarker}>
                <Icon name="status-alert" size={12} aria-hidden="true" />
              </span>
            )}
          </RawButton>
        )
        return option.error ? (
          <Tooltip key={option.value} content={option.error}>
            {tab}
          </Tooltip>
        ) : (
          tab
        )
      })}
    </span>
  )
}
